/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormInputProps, FormInput } from './FormInput';
import { SubmitButton, SubmitFormButtonProps } from './SubmitFormButton';
import { FormController, FormControllerProps } from './Controller';
import { FormWrapper, FormWrapperProps } from './Wrapper';

import { InferSchema, PossibleSchema, v } from 'utils/validation';
import { getLastIndex } from 'utils/array';
import { typedEntries } from 'utils/object';
import { StableOmit } from 'types';

type TypedComponent<Props> = (props: Props) => ReactElement;

function getTypedForm<T extends PossibleSchema>(schema: T) {
  const TypedForm = (props: StableOmit<FormWrapperProps<T>, 'schema'>) => (
    <FormWrapper schema={schema} {...props} />
  );

  return TypedForm;
}

export function getFormConfig<T extends Record<string, PossibleSchema>>(schemaObj: T) {
  const schema = v.object(schemaObj);

  type BaseFormSchema = typeof schema;

  type FormType = InferSchema<BaseFormSchema>;

  type SchemaRefiner = (formSchema: BaseFormSchema) => PossibleSchema;

  const base = {
    /**
     * WARNING! type only property
     */
    __type: {} as FormType,

    schema,

    Input: FormInput as TypedComponent<FormInputProps<FormType>>,

    SubmitButton: SubmitButton as TypedComponent<SubmitFormButtonProps<FormType>>,

    Controller: FormController as <Property extends keyof FormType>(
      props: FormControllerProps<FormType, Property>,
    ) => ReactElement,

    Form: getTypedForm(schema),

    useContext: () => {
      const result = useFormContext<FormType>();

      return {
        ...result,

        /**
         * Set multiple values at once, guaranteeing only 1
         * update to the state on the last final value change
         */
        setValues: (values: { [K in keyof FormType]?: FormType[K] }) => {
          const entries = typedEntries(values);
          const lastIndex = getLastIndex(entries);

          entries.forEach(([key, value], index) => {
            const isLast = index === lastIndex;

            result.setValue(key as any, value as any, isLast ? {} : { shouldValidate: false });
          });
        },
      };
    },
  };

  return {
    ...base,

    /**
     * Study other apis to propose and expose these...
     */
    refine: (refiner: SchemaRefiner) => {
      const result = refiner(schema);

      return {
        ...base,

        schema: result,
        Form: getTypedForm(result),
      };
    },
  };
}
